import { useCallback, useEffect, useRef, useState } from 'react';
import LoadingRows from './LoadingRows.jsx';
import TeamLogo from './TeamLogo';
import { getStandings } from '../services/standings';
import { ACTIVE_WOMENS_SEASON } from '../data/seasonConfig.js';
import { conferenceFreshness, formatCheckedDate } from '../data/dataFreshness.js';
import {
  CONFERENCE_FAVORITES_EVENT, TEAM_FAVORITES_EVENT, getConferenceFavoriteKey,
  getTeamFavoriteKey, getTeamFavoriteKeys, isConferenceFavorite, isTeamFavorite,
  readConferenceFavorites, readTeamFavorites, saveConferenceFavorites, saveTeamFavorites,
} from '../services/favorites';
import '../styles/standings.css';

const initialConference = () => new URLSearchParams(window.location.search).get('standingsConference') || 'all';
const recordLabel = record => record?.label || '—';
const stateLabels = {active:'Conference play underway',preconference:'Before conference results',partial:'Partial official table',unavailable:'Standings unavailable',unloaded:'Open standings'};

function checkedLabel(value) {
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? new Intl.DateTimeFormat('en-US', {
    month:'short',day:'numeric',hour:'numeric',minute:'2-digit',timeZoneName:'short',
  }).format(date) : '';
}

function Star({active, name, collection, onClick}) {
  return <button type="button" className={`conference-star${active ? ' active' : ''}`}
    aria-pressed={active} aria-label={`${active ? 'Remove' : 'Add'} ${name} ${active ? 'from' : 'to'} ${collection}`}
    onClick={onClick}><span aria-hidden="true">{active ? '★' : '☆'}</span></button>;
}

export function ConferenceStandingsTable({conference, favorites, onOpenTeam, onToggleTeam}) {
  const divisions = [...new Set(conference.teams.map(row => row.division || ''))];
  return <>{divisions.map(division => <div className="conference-table-group" key={division}>
    {division ? <h3>{division} Division</h3> : null}
    <table className="conference-table">
      <caption className="conference-visually-hidden">{conference.name} {division} regular-season standings</caption>
      <thead><tr><th scope="col" aria-label="Place">Pos.</th><th scope="col">Team</th>
        <th scope="col">{division ? 'Div / Conf' : 'Conf'}</th><th scope="col">Overall</th>
        <th scope="col"><span className="conference-visually-hidden">Favorite</span></th></tr></thead>
      <tbody>{conference.teams.filter(row => (row.division || '') === division).map(row => <tr key={row.id}>
        <td className="conference-place">{row.positionLabel || '—'}</td>
        <th scope="row"><button type="button" className="conference-team-link" onClick={() => onOpenTeam(row.team)}
          aria-label={`Open ${row.team.name} team profile`}>
          <span className="conference-logo"><TeamLogo team={row.team} variant="scoreboard" /></span>
          <span>{row.team.name}</span></button></th>
        <td><strong>{recordLabel(division ? row.divisionRecord : row.conferenceRecord)}</strong>
          <small>{row.conferencePctLabel || '—'}</small>
          {division ? <small>Conf {recordLabel(row.conferenceRecord)}</small> : null}</td>
        <td><strong>{recordLabel(row.overallRecord)}</strong></td>
        <td><Star active={isTeamFavorite(row.team,favorites)} name={row.team.name} collection="My Teams" onClick={() => onToggleTeam(row.team)} /></td>
      </tr>)}</tbody>
    </table>
  </div>)}</>;
}

export default function StandingsPage({onOpenTeam}) {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const refreshClock = () => { if (document.visibilityState === 'visible') setNow(Date.now()); };
    const timer = window.setInterval(refreshClock, 60000);
    document.addEventListener('visibilitychange', refreshClock);
    return () => { window.clearInterval(timer); document.removeEventListener('visibilitychange', refreshClock); };
  }, []);
  const [selected,setSelected] = useState(initialConference);
  const [directory,setDirectory] = useState([]);
  const [tables,setTables] = useState({});
  const [loading,setLoading] = useState(true);
  const [error,setError] = useState('');
  const [query,setQuery] = useState('');
  const [filter,setFilter] = useState('all');
  const [teamFavorites,setTeamFavorites] = useState(readTeamFavorites);
  const [conferenceFavorites,setConferenceFavorites] = useState(readConferenceFavorites);
  const requestRef = useRef(null);
  const sequence = useRef(0);
  const tablesRef = useRef(tables);
  tablesRef.current = tables;

  const load = useCallback(async (force = false) => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    const id = ++sequence.current;
    setLoading(true);setError('');
    try {
      const payload = await getStandings({conference:selected === 'all' ? '' : selected,signal:controller.signal,force});
      if (controller.signal.aborted || id !== sequence.current) return;
      if (selected === 'all') setDirectory(payload.conferences);
      else {
        const table = payload.conferences.find(item=>item.slug===selected);
        if (!table) throw new Error('This conference did not return a standings table.');
        const previous = tablesRef.current[selected];
        setTables(current=>({...current,[selected]:table.state === 'unavailable' && previous?.fetchedAt
          ? {...previous,stale:true,notice:'Refresh failed. Showing the previously checked table; records may have changed.'} : table}));
      }
    } catch(err) {
      if (controller.signal.aborted || id !== sequence.current) return;
      setError(err.message || 'The standings could not be refreshed.');
      setTables(current=>current[selected] ? {...current,[selected]:{...current[selected],stale:true}} : current);
    } finally {
      if (!controller.signal.aborted && id === sequence.current) setLoading(false);
    }
  },[selected]);

  useEffect(()=>{load();return ()=>requestRef.current?.abort();},[load]);
  useEffect(()=>{
    // Direct conference links also need the selector inventory.
    const controller = new AbortController();
    getStandings({signal:controller.signal}).then(payload=>{
      if(!controller.signal.aborted) setDirectory(payload.conferences);
    }).catch(()=>{ /* The selected conference can still load independently. */ });
    return ()=>controller.abort();
  },[]);
  useEffect(()=>{
    const refresh = ()=>{if(document.visibilityState === 'visible') load(true);};
    const interval=setInterval(refresh,300000);
    document.addEventListener('visibilitychange',refresh);
    return ()=>{clearInterval(interval);document.removeEventListener('visibilitychange',refresh);};
  },[load]);
  useEffect(()=>{
    const pop=()=>{setSelected(initialConference());setQuery('');setFilter('all');};
    const favorites=()=>{setTeamFavorites(readTeamFavorites());setConferenceFavorites(readConferenceFavorites());};
    window.addEventListener('popstate',pop);
    for(const event of ['storage',TEAM_FAVORITES_EVENT,CONFERENCE_FAVORITES_EVENT]) window.addEventListener(event,favorites);
    return ()=>{window.removeEventListener('popstate',pop);for(const event of ['storage',TEAM_FAVORITES_EVENT,CONFERENCE_FAVORITES_EVENT]) window.removeEventListener(event,favorites);};
  },[]);

  function selectConference(slug) {
    setSelected(slug);setQuery('');setFilter('all');setError('');
    const url=new URL(window.location.href);
    for(const key of ['sport','team','teamSeason','match','rankingSeason']) url.searchParams.delete(key);
    if(slug==='all') url.searchParams.delete('standingsConference');else url.searchParams.set('standingsConference',slug);
    url.hash='standings';window.history.pushState({},'',url);
  }
  function toggleTeam(team) {
    const keys=getTeamFavoriteKeys(team), primary=getTeamFavoriteKey(team);
    const active=keys.some(key=>teamFavorites.includes(key));
    const next=active ? teamFavorites.filter(key=>!keys.includes(key)) : [...new Set([...teamFavorites,primary].filter(Boolean))];
    setTeamFavorites(next);saveTeamFavorites(next);
  }
  function toggleConference(conference) {
    const key=getConferenceFavoriteKey(conference);
    if(!key) return;
    const next=conferenceFavorites.includes(key) ? conferenceFavorites.filter(item=>item!==key) : [...conferenceFavorites,key];
    setConferenceFavorites(next);saveConferenceFavorites(next);
  }
  function openTeam(team) {
    if(onOpenTeam) return onOpenTeam(team);
    const url=new URL(window.location.href);
    url.searchParams.delete('sport');url.searchParams.delete('match');
    url.searchParams.set('team',team.slug);url.searchParams.set('teamSeason',String(ACTIVE_WOMENS_SEASON));url.hash='teams';
    window.history.pushState({},'',url);window.dispatchEvent(new PopStateEvent('popstate'));
  }
  const favoriteSet=new Set(teamFavorites), conferenceSet=new Set(conferenceFavorites);
  const normalizedQuery=query.trim().toLowerCase();
  const table=tables[selected];
  const freshness=conferenceFreshness(table?.fetchedAt,now);
  const visibleDirectory=directory.map(item=>tables[item.slug] || item).filter(item=>(filter!=='conferences'||isConferenceFavorite(item,conferenceSet)) &&
    (filter!=='favorites'||item.teams.some(row=>isTeamFavorite(row.team,favoriteSet))) &&
    (!normalizedQuery||[item.name,...item.teams.map(row=>row.team.name)].join(' ').toLowerCase().includes(normalizedQuery)))
    .sort((a,b)=>Number(isConferenceFavorite(b,conferenceSet))-Number(isConferenceFavorite(a,conferenceSet))||a.name.localeCompare(b.name));
  const visibleTable=table ? {...table,teams:table.teams.filter(row=>(filter!=='favorites'||isTeamFavorite(row.team,favoriteSet)) &&
    (!normalizedQuery||[row.team.name,...(row.team.aliases||[])].join(' ').toLowerCase().includes(normalizedQuery)))} : null;

  return <main className="standings-page conference-standings-v2" id="standings"><section className="standings-section"><div className="shell">
    <div className="conference-page-heading"><div><p className="eyebrow coral">{ACTIVE_WOMENS_SEASON} · Women’s Division I</p><h1>Conference Standings</h1></div>
      <button type="button" className="conference-action" onClick={()=>load(true)} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh'}</button></div>
    <div className="conference-controls">
      <label><span className="conference-visually-hidden">Conference</span><select value={selected} onChange={event=>selectConference(event.target.value)}>
        <option value="all">All conferences</option>{directory.map(item=><option key={item.slug} value={item.slug}>{item.name}</option>)}
        {selected!=='all'&&!directory.some(item=>item.slug===selected) ? <option value={selected}>{selected}</option> : null}
      </select></label>
      <label><span className="conference-visually-hidden">Search conferences or teams</span><input type="search" placeholder="Search teams or conferences" value={query} onChange={event=>setQuery(event.target.value)} /></label>
    </div>
    <div className="conference-filters" aria-label="Standings filters">{[['all','All'],['favorites','My Teams'],...(selected==='all' ? [['conferences','My Conferences']] : [])].map(([value,label])=>
      <button key={value} type="button" aria-pressed={filter===value} onClick={()=>setFilter(value)}>{label}</button>)}</div>
    {selected!=='all' ? <button type="button" className="conference-action" onClick={()=>selectConference('all')}>‹ All conferences</button> : null}
    {error ? <div role="alert" className="conference-status"><p>{error}{table ? ' Previously loaded records are retained.' : ''}</p><button type="button" className="conference-action" onClick={()=>load(true)}>Try again</button></div> : null}
    {loading && (selected==='all' ? !directory.length : !table) ? <LoadingRows label="Loading conference standings…" /> : null}
    {selected==='all' ? <div className="standings-directory" aria-label="Conference directory">{visibleDirectory.map(item=>
      <article key={item.slug} className="conference-directory-item"><button type="button" aria-label={`View ${item.name} conference standings`} onClick={()=>selectConference(item.slug)}>
        <strong>{item.name}</strong><span>{tables[item.slug] ? stateLabels[tables[item.slug].state] : 'View standings'} · {item.teamCount || '—'} teams</span></button>
        <Star active={isConferenceFavorite(item,conferenceSet)} name={item.name} collection="My Conferences" onClick={()=>toggleConference(item)} />
      </article>)}</div> : table ? <section aria-label={`${table.name} standings`} aria-busy={loading} className="conference-detail">
      <div className="conference-detail-heading"><h2>{table.name}</h2><Star active={isConferenceFavorite(table,conferenceSet)} name={table.name} collection="My Conferences" onClick={()=>toggleConference(table)} /></div>
      <div className={`conference-status${table.stale || freshness.aging ? ' stale' : ''}`} role="status"><strong>{table.stale ? 'Showing saved standings' : stateLabels[table.state]}</strong>
        {table.fetchedAt ? <strong className="conference-checked" title={formatCheckedDate(table.fetchedAt)}>{freshness.label}</strong> : null}
        {table.stale || freshness.aging ? <p>Recent results may be missing. Last successful check: {checkedLabel(table.fetchedAt)}.</p> : table.state==='partial' || table.state==='unavailable' ? <p>{table.notice}</p> : <small>Official publication may lag completed matches.</small>}
        {table.state==='unavailable' ? <button type="button" className="conference-action" onClick={()=>load(true)}>Try again</button> : null}
      </div>
      {visibleTable.teams.length ? <ConferenceStandingsTable conference={visibleTable} favorites={favoriteSet} onOpenTeam={openTeam} onToggleTeam={toggleTeam} /> : <p className="conference-empty">{table.teams.length ? 'No teams match these filters.' : 'No standings table is available. Please check the official source or retry.'}</p>}
      <details className="conference-rules"><summary>Source, standings and tiebreak rules</summary><p>{table.notice}</p>
        {table.fetchedAt ? <p>Source checked {formatCheckedDate(table.fetchedAt)}.</p> : null}
        {table.sourceUrl ? <a href={table.sourceUrl} target="_blank" rel="noopener noreferrer">{table.source || 'Official conference source'} ↗</a> : null}
        <p>{table.rulesNote}</p><p>Regular-season titles and tournament seeds are separate. Conference-specific postseason tiebreakers, eligibility restrictions and administrative decisions are not guessed from these records.</p></details>
    </section> : null}
    {!loading && selected==='all'&&!visibleDirectory.length ? <p className="conference-empty">No conferences match. Try another search or favorite filter.</p> : null}
  </div></section></main>;
}
